import { useTranslation } from "react-i18next";
import {
  AutoCompleteMulti,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { insuranceRegisterList } from "../../../../../../../services/ticketAutomationReceivedRuleInsurance.service";
import {
  createTicketAutomationReceivedDaysAhead,
  getTicketAutomationReceivedDaysAheadById,
  updateTicketAutomationReceivedDaysAheadById,
} from "../../../../../../../services/ticketAutomationReceivedDaysAhead.service";
import {
  automationReceivedConfFileSchema,
  TAutomationReceivedDaysAheadForm,
} from "./automationReceivedDaysAheadSchema";
import { IAutomationReceivedDaysAheadProps } from "./models";
import { DaysAheadInternalProcedure } from "./DaysAheadInternalProcedure/DaysAheadInternalProcedure";
import { DaysAheadCalendars } from "./DaysAheadCalendars/DaysAheadCalendars";
import { LimitFinalDate } from "./LimitFinalDate/LimitFinalDate";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedDaysAhead.";

const AutomationReceivedDaysAhead = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
  automationCode,
}: IAutomationReceivedDaysAheadProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedDaysAhead,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedDaysAheadById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedDaysAheadById,
  );

  const [
    optionsTicketAutomationReceivedDaysAheadInsurances,
    setTicketAutomationReceivedDaysAheadCaleInsurances,
  ] = useState<ISelectType[]>([]);

  const { sendRequest: insuranceRequest } = useFetch(insuranceRegisterList);

  const updateHandler = (v: TAutomationReceivedDaysAheadForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        qtDays: Number(v.qtDays),
        enable: v.enable,
        limitMonths: Number(v.limitMonths),
        timeLimitTypeId: v.timeLimitType?.id,
        ticketAutomationReceivedDaysAheadCalendars:
          v.ticketAutomationReceivedDaysAheadCalendars
            ? v.ticketAutomationReceivedDaysAheadCalendars.map((cal) => ({
                externalId: cal.id,
                externalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedDaysAheadInsurances:
          v.ticketAutomationReceivedDaysAheadInsurances
            ? v.ticketAutomationReceivedDaysAheadInsurances.map((cal) => ({
                externalId: cal.id,
                externalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedDaysAheadProcedures:
          v.ticketAutomationReceivedDaysAheadProcedures
            ? v.ticketAutomationReceivedDaysAheadProcedures.map((cal) => ({
                externalId: cal.id,
                externalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TAutomationReceivedDaysAheadForm) =>
    save({
      ...v,
      qtDays: Number(v.qtDays),
      enable: v.enable,
      limitMonths: Number(v.limitMonths),
      timeLimitTypeId: v.timeLimitType?.id,
      ticketAutomationReceivedDaysAheadCalendars:
        v.ticketAutomationReceivedDaysAheadCalendars
          ? v.ticketAutomationReceivedDaysAheadCalendars.map((cal) => ({
              externalId: cal.id,
              externalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedDaysAheadInsurances:
        v.ticketAutomationReceivedDaysAheadInsurances
          ? v.ticketAutomationReceivedDaysAheadInsurances.map((cal) => ({
              externalId: cal.id,
              externalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedDaysAheadProcedures:
        v.ticketAutomationReceivedDaysAheadProcedures
          ? v.ticketAutomationReceivedDaysAheadProcedures.map((cal) => ({
              externalId: cal.id,
              externalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TAutomationReceivedDaysAheadForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TAutomationReceivedDaysAheadForm = {
          ...data,
          limitMonths: data.limitMonths?.toString(),
          qtDays: data.qtDays.toString(),
          enable: data.enable,
          ticketAutomationReceivedDaysAheadCalendars:
            data.ticketAutomationReceivedDaysAheadCalendars
              ? data.ticketAutomationReceivedDaysAheadCalendars.map((cal) => ({
                  id: cal.externalId,
                  name: cal.externalName,
                }))
              : undefined,
          ticketAutomationReceivedDaysAheadProcedures:
            data.ticketAutomationReceivedDaysAheadProcedures
              ? data.ticketAutomationReceivedDaysAheadProcedures.map((cal) => ({
                  id: cal.externalId,
                  name: cal.externalName,
                }))
              : undefined,
          ticketAutomationReceivedDaysAheadInsurances:
            data.ticketAutomationReceivedDaysAheadInsurances
              ? data.ticketAutomationReceivedDaysAheadInsurances.map((cal) => ({
                  id: cal.externalId,
                  name: cal.externalName,
                }))
              : undefined,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const tasyScheduleTypeCode = useMemo(() => {
    if (!automationCode) return;

    switch (automationCode) {
      case "21":
        return [2];
      default:
        return [3];
    }
  }, [automationCode]);

  const classificationConsultationIdentifier = useMemo(() => {
    if (!automationCode) return;

    switch (automationCode) {
      case "25":
        return ["R"];
      default:
        return null;
    }
  }, [automationCode]);

  const params = useMemo(() => {
    return {
      EnableCode: true,
      TasyScheduleTypeCode: tasyScheduleTypeCode,
      ClassificationConsultationIdentifier:
        classificationConsultationIdentifier,
    };
  }, [tasyScheduleTypeCode, classificationConsultationIdentifier]);

  const paramsInsurance = useMemo(() => {
    return {
      EnableCode: true,
    };
  }, []);

  const fetchticketAutomationReceivedDaysAheadCaleInsurances =
    useCallback(async () => {
      const { data, success } = await insuranceRequest(paramsInsurance);
      if (data && success) {
        setTicketAutomationReceivedDaysAheadCaleInsurances(
          data.map((d) => ({
            id: d.identifier.toString(),
            name: d.insuranceName,
          })),
        );
      }
    }, [insuranceRequest, paramsInsurance]);

  useEffect(() => {
    fetchticketAutomationReceivedDaysAheadCaleInsurances();
  }, [fetchticketAutomationReceivedDaysAheadCaleInsurances]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TAutomationReceivedDaysAheadForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "days_ahead")}
      schema={automationReceivedConfFileSchema}
      subtitle={t(translationPath + "new_days_ahead")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      defaultValues={{ automationCode: automationCode }}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item sm={12} textAlign={"end"}>
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item sm={12}>
            <Typography fontWeight="bold">
              {t(translationPath + "if")}
            </Typography>
          </Grid>

          {automationCode && ["20", "25"].includes(automationCode) && (
            <DaysAheadCalendars automationCode={automationCode} />
          )}

          {automationCode === "21" && (
            <DaysAheadInternalProcedure
              automationCode={automationCode}
              params={params}
            />
          )}

          <Grid item xs={12} sm={5}>
            <AutoCompleteMulti
              params={params}
              fullWidth
              options={optionsTicketAutomationReceivedDaysAheadInsurances}
              name="ticketAutomationReceivedDaysAheadInsurances"
              label={t(
                translationPath + "ticketAutomationReceivedDaysAheadInsurances",
              )}
              getOptionLabel={(option) => option.name}
              size="small"
              keepOpenOnSelect={true}
            />
          </Grid>

          <Grid item sm={12}>
            <Typography fontWeight="bold">
              {t(translationPath + "then")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              type="number"
              size="small"
              name="qtDays"
              fullWidth
              label={t(translationPath + "qtd_days") || "qtDays"}
            />
          </Grid>

          <LimitFinalDate />
        </Grid>
      )}
    />
  );
};

export default AutomationReceivedDaysAhead;
